/**
 * Tab „Lagedaten & Kennzahlen": durchgehende, userfreundliche Vergleichstabelle
 * (Gemeinde/Kreis/Land/Bund) + Markdown-Download.
 * Ersetzt den früheren „Lagedaten"-Tab mit dem rohen Textblock.
 */

import { Fragment, useState } from 'react'
import { Check, ClipboardCopy, Download, Loader2 } from 'lucide-react'
import { useMunicipalityLageprompt } from '../../hooks/queries'
import { api } from '../../services/api'
import type { LagedatenBereich, LagedatenRow, LagedatenTable } from '../../types/api'

interface Props {
  municipalityKey: string
}

const COLS = 6

function cell(value: string | null | undefined) {
  return value == null || value === '' ? '–' : value
}

/** Prüft, ob eine Zeile eine Vergleichszeile (Ebenen-Spalten) ist. */
function isCmpRow(r: LagedatenRow): boolean {
  return r.wert == null
}

function RatingBadge({ term }: { term?: string | null }) {
  if (!term) return null
  return (
    <span className="inline-block bg-primary text-text-inverse rounded-full px-2.5 py-0.5 text-xs font-semibold">
      „{term}"
    </span>
  )
}

function BereichRows({ bereich }: { bereich: LagedatenBereich }) {
  return (
    <>
      <tr>
        <td colSpan={COLS} className="bg-gut8-bg-primary text-text-inverse font-semibold px-3 py-2">
          <span className="flex items-center justify-between gap-2">
            <span>{bereich.titel}</span>
            <RatingBadge term={bereich.rating?.term} />
          </span>
        </td>
      </tr>
      {bereich.hinweis && (
        <tr>
          <td colSpan={COLS} className="bg-primary-subtle text-gut8-anthracite text-xs px-3 py-2 leading-snug">
            {bereich.hinweis}
          </td>
        </tr>
      )}
      {bereich.subgruppen.map((sg, si) => {
        const rows = sg.zeilen.filter(Boolean)
        if (rows.length === 0) return null
        return (
          <Fragment key={`sg-${si}`}>
            {sg.label && (
              <tr>
                <td colSpan={COLS} className="px-3 pt-3 pb-1">
                  <span className="text-xs font-semibold text-primary-dark uppercase tracking-wider">
                    {sg.label}
                  </span>
                  {sg.rating?.term && (
                    <span className="ml-2 text-xs text-text-muted">„{sg.rating.term}"</span>
                  )}
                </td>
              </tr>
            )}
            {rows.map((r, ri) => (
              ('text' in r && r.text != null) ? (
                <tr key={`r-${si}-${ri}`} className="border-t border-border-gut8-light">
                  <td colSpan={COLS} className="px-3 py-2 text-text-muted italic leading-snug">{r.text}</td>
                </tr>
              ) : (
              <tr key={`r-${si}-${ri}`} className="border-t border-border-gut8-light">
                <td className="px-3 py-1.5 text-gut8-anthracite">{r.label}</td>
                {isCmpRow(r) ? (
                  <>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gut8-anthracite">{cell(r.gemeinde)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gut8-anthracite">{cell(r.kreis)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-text-muted">{cell(r.land)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-text-muted">{cell(r.bund)}</td>
                    <td className="px-3 py-1.5 text-text-muted whitespace-nowrap">{cell(r.stand)}</td>
                  </>
                ) : (
                  <>
                    <td colSpan={4} className="px-3 py-1.5 text-gut8-anthracite">{cell(r.wert)}</td>
                    <td className="px-3 py-1.5 text-text-muted whitespace-nowrap">{cell(r.stand)}</td>
                  </>
                )}
              </tr>
              )
            ))}
          </Fragment>
        )
      })}
    </>
  )
}

function LagedatenTable({ table }: { table: LagedatenTable }) {
  return (
    <div className="card-accent overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left">
            <th className="px-3 pb-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Kennzahl</th>
            <th className="px-3 pb-2 text-right text-xs font-semibold text-text-muted uppercase tracking-wider">Gemeinde</th>
            <th className="px-3 pb-2 text-right text-xs font-semibold text-text-muted uppercase tracking-wider">Kreis</th>
            <th className="px-3 pb-2 text-right text-xs font-semibold text-text-muted uppercase tracking-wider">Land</th>
            <th className="px-3 pb-2 text-right text-xs font-semibold text-text-muted uppercase tracking-wider">Bund</th>
            <th className="px-3 pb-2 text-xs font-semibold text-text-muted uppercase tracking-wider">Stand</th>
          </tr>
        </thead>
        <tbody>
          {table.gruppen.map((gruppe, gi) => (
            <Fragment key={`g-${gi}`}>
              <tr>
                <td colSpan={COLS} className="pt-5 pb-2">
                  <span className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gut8-anthracite">{gruppe.titel}</span>
                    <RatingBadge term={gruppe.rating?.term} />
                  </span>
                </td>
              </tr>
              {gruppe.bereiche.map((bereich, bi) => (
                <BereichRows key={`b-${gi}-${bi}`} bereich={bereich} />
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function LagedatenKennzahlenTab({ municipalityKey }: Props) {
  const { data, isLoading, error } = useMunicipalityLageprompt(municipalityKey)
  const [copied, setCopied] = useState(false)

  const mdUrl = api.lagedatenMarkdownUrl(municipalityKey)

  const handleCopy = async () => {
    const res = await fetch(mdUrl)
    const text = await res.text()
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Download per fetch + Blob statt <a href> auf die API: die Link-Navigation
  // liefe am Auth-Interceptor vorbei (401 hinter Basic-Auth) und führte den
  // Browser sichtbar auf den API-Host statt unter der Site-Domain zu bleiben.
  const handleDownload = async () => {
    const res = await fetch(mdUrl)
    if (!res.ok) return
    const blob = await res.blob()
    const match = (res.headers.get('Content-Disposition') || '').match(/filename="?([^";]+)"?/)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = match?.[1] || `Lagedaten_${municipalityKey}.md`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-text-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lagedaten werden zusammengestellt …
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        Lagedaten konnten nicht geladen werden.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Aktionen */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-gut8-anthracite">Kennzahlen im Vergleich</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Als Markdown (.md)
          </button>
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-gut8 border border-border-gut8-medium text-gut8-anthracite hover:bg-primary-subtle transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <ClipboardCopy className="w-4 h-4" />}
            {copied ? 'Kopiert' : 'Kopieren'}
          </button>
        </div>
      </div>

      {/* Vergleichstabelle */}
      <LagedatenTable table={data.lagedaten_table} />
    </div>
  )
}
